// 覚醒王・大暴れ第5案の「床の衝撃波」と「ピカン」の見た目 — 3案 (a/b/c) と現状 (now) を実行時に切り替える。
//
// 🔵 2026-09-20 ユーザー「そのうえで絵とか細かいエフェクトなどの話を詰めて行きたい」の ①衝撃波 ⑤ピカン。
//   切替 = `?waveFx=now|a|b|c` / `?glintFx=now|a|b|c`。判定・秒数は一切変えない (ここは描くだけ)。
//   絵そのものを作り直す案も残っている (ユーザー「それぞれで絵は作り直すことも視野」) ので、
//   ここは「暫定の見た目をコードだけで比べる」ための場所。
//
// 3案に共通の縛り:
//   ① 🔴 線 (stroke) で描かない。面と粒で作る (2026-09-03 介錯の地割りでのユーザー裁定
//      「線っぽいエフェクトは基本ダサい」。`quake_fx.ts` 冒頭と同じ縛り)。
//   ② 赤 = 弾けない・避ける は全ボス共通の視覚言語。衝撃波は弾けないので赤を必ず残す。
//   ③ ピカンは「跳び出しの 0.45 秒前に光って、動き出しを予告する」役。色は赤=床技 / 白=締めの噛み。
import type { ActiveHit } from './types'
import { glowMote, hash01 } from './fx_kit'
import { getFloorWave, getGrudgeOrb, getGrudgeSheet, GRUDGE_SHEET_FRAMES, preloadGrudgeSheet } from './fx_sprites'
import { sw as swc, swapImage } from './purple_swap'
import { drawAwokenWaveRift, getAwokenWaveFx } from './awoken_wave_rift_fx'

/** 衝撃波の見た目。art = 石の割れを描き起こした絵 (2026-09-22 発注)。a = 09-20 裁定のコード描き (砕けた床)。 */
export type RampageWaveLook = 'now' | 'art' | 'a' | 'b' | 'c'
export type RampageGlintLook = 'now' | 'a' | 'b' | 'c'
/** 怨念の見た目。art = 床の帯と同じ画風で描き起こした絵 (2026-09-20 発注)。a/b/c はコードで描いた比較案。 */
export type RampageSpiritLook = 'now' | 'art' | 'a' | 'b' | 'c'

const LOOKS: readonly string[] = ['now', 'a', 'b', 'c']
const SPIRIT_LOOKS: readonly string[] = ['now', 'art', 'a', 'b', 'c']
// 既定 = 2026-09-20 ユーザー裁定 (①衝撃波 = a 砕けた床 / ⑤ピカン = b 輪の破裂)。
//   外した案と現状は `?waveFx=` / `?glintFx=` に残す (now = 09-20 までの見た目)。
function readLook(param: string, fallback: 'now' | 'a' | 'b' | 'c'): 'now' | 'a' | 'b' | 'c' {
  if (!import.meta.env.DEV || typeof window === 'undefined') return fallback
  const v = new URLSearchParams(window.location.search).get(param)
  return LOOKS.includes(v ?? '') ? v as 'now' | 'a' | 'b' | 'c' : fallback
}
// 衝撃波の既定 = art (2026-09-22 ユーザー試遊のうえ裁定「絵のほうで採用」)。09-20 の a は `?waveFx=a` で比較・戻し用に残す。
let waveLook: RampageWaveLook = (() => {
  if (!import.meta.env.DEV || typeof window === 'undefined') return 'art'
  const v = new URLSearchParams(window.location.search).get('waveFx')
  return v === 'art' || v === null ? 'art' : readLook('waveFx', 'a')
})()
let glintLook: RampageGlintLook = readLook('glintFx', 'b')
let spiritLook: RampageSpiritLook = (() => {
  if (!import.meta.env.DEV || typeof window === 'undefined') return 'art'
  const v = new URLSearchParams(window.location.search).get('spiritFx')
  return SPIRIT_LOOKS.includes(v ?? '') ? v as RampageSpiritLook : 'art'
})()
export function setRampageWaveLook(look: RampageWaveLook): void { waveLook = look }
export function getRampageWaveLook(): RampageWaveLook { return waveLook }
export function setRampageGlintLook(look: RampageGlintLook): void { glintLook = look }
export function getRampageGlintLook(): RampageGlintLook { return glintLook }
export function setRampageSpiritLook(look: RampageSpiritLook): void { spiritLook = look }
export function getRampageSpiritLook(): RampageSpiritLook { return spiritLook }

const clamp01 = (v: number): number => v < 0 ? 0 : v > 1 ? 1 : v

/** 怨念の漂い (2026-09-20 ユーザー「漂ってるようにして欲しい」「見た目だけ」)。
 *  強さ = `?spiritDrift=off|a|b`。off = 09-20 までの静止、a = 控えめ、b = しっかり (既定)。 */
export type RampageSpiritDrift = 'off' | 'a' | 'b'
const DRIFTS: readonly string[] = ['off', 'a', 'b']
const DRIFT_GAIN: Readonly<Record<RampageSpiritDrift, number>> = { off: 0, a: 0.55, b: 1 }
let spiritDrift: RampageSpiritDrift = (() => {
  if (!import.meta.env.DEV || typeof window === 'undefined') return 'b'
  const v = new URLSearchParams(window.location.search).get('spiritDrift')
  return DRIFTS.includes(v ?? '') ? v as RampageSpiritDrift : 'b'
})()
export function setRampageSpiritDrift(d: RampageSpiritDrift): void { spiritDrift = d }
export function getRampageSpiritDrift(): RampageSpiritDrift { return spiritDrift }

/** 怨念の大きさ (当たり半径 r の何倍で絵を貼るか)。`?spiritSize=s|m|l`。
 *  s = 09-20 の 4.2 倍、m = 既定、l = 大きめ。判定 (26px) は変えない。 */
const SPIRIT_SIZES: readonly string[] = ['s', 'm', 'l']
const SPIRIT_SIZE_MUL: Readonly<Record<string, number>> = { s: 4.2, m: 5.6, l: 7.0 }
const spiritSizeKey: string = (() => {
  if (!import.meta.env.DEV || typeof window === 'undefined') return 'm'
  const v = new URLSearchParams(window.location.search).get('spiritSize')
  return SPIRIT_SIZES.includes(v ?? '') ? v! : 'm'
})()

/** 漂いの揺れ幅 (見た目だけ。hit.x/y = 当たりの位置は動かさない)。
 *  溜めの間 = その場で8の字に浮く。飛んでいる間 = 進む向きと直角に蛇行し、頭も揺れへ傾く。
 *  飛び出しの前後で揺れ方が切り替わるので、0.18 秒かけて混ぜる (瞬間的に跳ばないように)。 */
function spiritDriftAt(time: number, id: number, r: number, ang: number, flyingMs: number):
  { dx: number; dy: number; rot: number } {
  const gain = DRIFT_GAIN[spiritDrift]
  if (gain <= 0) return { dx: 0, dy: 0, rot: 0 }
  const p = id * 1.37
  const hang = {
    dx: r * 0.95 * Math.sin(time * 1.15 + p),
    dy: r * 0.70 * Math.sin(time * 1.73 + p * 1.7) + r * 0.22 * Math.sin(time * 0.61 + p),
    rot: 0.28 * Math.sin(time * 1.4 + p),
  }
  const sway = r * 0.45 * Math.sin(time * 6.4 + p)
  const fly = {
    dx: -Math.sin(ang) * sway, dy: Math.cos(ang) * sway,
    rot: 0.20 * Math.cos(time * 6.4 + p),
  }
  const m = clamp01(flyingMs / 180)
  return {
    dx: (hang.dx + (fly.dx - hang.dx) * m) * gain,
    dy: (hang.dy + (fly.dy - hang.dy) * m) * gain,
    rot: (hang.rot + (fly.rot - hang.rot) * m) * gain,
  }
}

// ===== ① 床の衝撃波 (弾けない・回避で抜ける) =====

/** 床を這う衝撃波。hit.x/y は帯の中心、r は帯の半幅、floor = 床の高さ。 */
export function drawRampageWave(ctx: CanvasRenderingContext2D, hit: ActiveHit, time: number): void {
  const s = hit.spirit!
  const x = hit.x!, y = hit.y!, r = (hit.widthPx ?? 26) / 2
  const dir = Math.sign(hit.vx ?? 1) || 1
  const fade = Math.max(0.15, Math.min(1, (s.rangePx - s.traveledPx) / 60))
  // hit.y は帯の中心 (床から heightPx/2)。横幅の半分 r を足すと床から 7px 浮く (09-21 Codex 指摘) ので、高さの半分を足す。
  const floor = y + WAVE_H / 2
  // 10-20 作者の選択「紫の虚の割れ」(awoken_wave_rift_fx.ts)。前の絵は DEV `?awokenWaveFx=old`・絵が読めていない間も前で描く。
  if (getAwokenWaveFx() === 'rift' && drawAwokenWaveRift(ctx, hit, time, floor, fade)) return
  const seed = hit.id * 7.31
  ctx.save()
  ctx.globalAlpha = fade
  if (waveLook === 'art' && drawWaveArt(ctx, x + dir * r, floor, dir, s.traveledPx, time, hit.id)) { /* 絵 */ }
  else if (waveLook === 'now') drawWaveNow(ctx, x, floor, r, dir, time, hit.id)
  else if (waveLook === 'a') drawWaveRubble(ctx, x, floor, dir, seed, s.traveledPx)
  else if (waveLook === 'b') drawWaveCurse(ctx, x, floor, dir, seed, s.traveledPx)
  else drawWaveSoul(ctx, x, floor, dir, seed, s.traveledPx)
  ctx.restore()
}

// 絵の案 (2026-09-22 票 §3)。先端の足元 (絵の 440,300) を当たる帯の前端・床へ合わせる。
//   0.3 倍に縮める。票の見込み (約1/5) では石の盛り上がりが帯の高さ34pxの7割ほどで、今の a より小さく読みにくかった (09-22 実画面)。判定・速さは変えない。
const WAVE_ART_SCALE = 0.3
const WAVE_ART_ANCHOR_X = 440, WAVE_ART_ANCHOR_Y = 300
const WAVE_ART_FPS = 12
/** 絵が読み込めていなければ false (呼び出し側でコード描きへ落とす)。 */
function drawWaveArt(ctx: CanvasRenderingContext2D, frontX: number, floor: number, dir: number, traveled: number, time: number, id: number): boolean {
  // 走り出し 120px (0.2秒) は生まれた瞬間のコマ、以後は 0..2 を回す。
  const frame = traveled < 120 ? 3 : Math.floor(time * WAVE_ART_FPS + id) % 3
  const img = getFloorWave(frame)
  if (!img) return false
  ctx.save()
  ctx.translate(frontX, floor)
  ctx.scale(dir * WAVE_ART_SCALE, WAVE_ART_SCALE)
  ctx.drawImage(img, -WAVE_ART_ANCHOR_X, -WAVE_ART_ANCHOR_Y)
  ctx.restore()
  return true
}

/** 現状 (2026-09-20 まで): 赤い三角の波。前が高く後ろへ尾を引く。線あり。 */
function drawWaveNow(ctx: CanvasRenderingContext2D, x: number, floor: number, r: number, dir: number, time: number, id: number): void {
  ctx.fillStyle = '#d8323a'; ctx.strokeStyle = '#ffb0a8'; ctx.lineWidth = 2
  ctx.beginPath()
  ctx.moveTo(x - dir * r * 3, floor)
  ctx.quadraticCurveTo(x - dir * r * 0.6, floor - r * 0.7, x, floor - r * 2 * (0.9 + 0.1 * Math.sin(time * 20 + id)))
  ctx.quadraticCurveTo(x + dir * r * 0.5, floor - r * 0.8, x + dir * r * 0.9, floor)
  ctx.closePath(); ctx.fill(); ctx.stroke()
}

/** 面を潰して描く光 (床に寝た光・低い波に使う)。glowMote は真円なので縦に潰して使う。 */
function flatMote(ctx: CanvasRenderingContext2D, x: number, y: number, rx: number, ry: number, color: string, a: number): void {
  ctx.save()
  ctx.translate(x, y)
  ctx.scale(1, ry / rx)
  glowMote(ctx, 0, 0, rx, color, a)
  ctx.restore()
}

// 3案に共通の寸法: 当たるのは先端の帯 (幅20px) だけ。尾は「もう通り過ぎた跡」なので後ろへだけ伸ばす。
const WAVE_H = 34        // 帯の高さ (WAVE.heightPx と同じ)
const WAVE_TAIL = 104    // 尾の最大の長さ

/** 3案に共通の形: 床を走る低い波。前が立ち上がり、後ろへ長く薄く引く (丸い玉に見せない)。 */
function crestPath(ctx: CanvasRenderingContext2D, x: number, floor: number, dir: number, len: number, h: number): void {
  ctx.beginPath()
  ctx.moveTo(x + dir * 11, floor + 2)                                   // 先端の足元 (当たる帯の前)
  ctx.quadraticCurveTo(x + dir * 12, floor - h * 0.9, x + dir * 2, floor - h)   // 立ち上がり
  ctx.quadraticCurveTo(x - dir * len * 0.35, floor - h * 0.7, x - dir * len * 0.72, floor - h * 0.22)
  ctx.quadraticCurveTo(x - dir * len * 0.9, floor - h * 0.06, x - dir * len, floor + 2)  // 尾
  ctx.closePath()
}

/** 案a「砕けた床」: 石畳がめくれて走る。破片と土煙。赤は先端の熱だけ (面積を絞る)。
 *  09-21 総評 (Codex 優先3・Fable B-3): 「今どこまで来たか」= 走っている先端を強くする。
 *    ① 走り出し 0.2 秒は 1.5 倍の高さから潰れる (叩きつけの勢い) ② 先端の面に明るい石の縁と赤い接地光
 *    ③ 後ろの土煙は薄く ④ 四角い粒 → 回る三角の破片。線は使わない。 */
function drawWaveRubble(ctx: CanvasRenderingContext2D, x: number, floor: number, dir: number, seed: number, traveled: number): void {
  const len = Math.min(WAVE_TAIL, 40 + traveled * 0.5)
  const burst = clamp01(1 - traveled / 120)            // 走り出し 0.2 秒 (600px/秒)
  const h = WAVE_H * (1 + 0.5 * burst)
  ctx.globalCompositeOperation = 'source-over'
  // 土煙 (面・波の後ろに薄く)。先端より目立たせない。
  for (let i = 0; i < 3; i++) {
    const back = (i + 1) / 3
    ctx.fillStyle = `rgba(112,102,100,${(0.15 - i * 0.04).toFixed(3)})`
    ctx.beginPath()
    ctx.ellipse(x - dir * len * back * 0.6, floor - h * (0.5 + back * 0.45), len * back * 0.34, h * (0.35 + back * 0.3), 0, 0, Math.PI * 2)
    ctx.fill()
  }
  // 先端の熱 (赤) は石の下に敷く = 割れ目から漏れる光に見せる。先端の足元に低く広い接地光を足す。
  ctx.globalCompositeOperation = 'lighter'
  flatMote(ctx, x + dir * 2, floor - h * 0.42, 38, 27, 'rgb(216,50,58)', 0.62)
  flatMote(ctx, x + dir * 14, floor - 3, 46, 9, 'rgb(255,96,72)', 0.7)
  ctx.globalCompositeOperation = 'source-over'
  // めくれた石畳 (面)。先端は前へ倒れ込む形 (垂直に立てると墓石に見えた・09-21 実画面)。
  const tipX = x + dir * 17, tipY = floor - h, footX = x + dir * 7
  ctx.fillStyle = 'rgb(58,54,62)'
  ctx.beginPath()
  ctx.moveTo(footX, floor + 2)
  ctx.lineTo(tipX, tipY)
  ctx.quadraticCurveTo(x - dir * len * 0.3, floor - h * 0.62, x - dir * len * 0.72, floor - h * 0.2)
  ctx.quadraticCurveTo(x - dir * len * 0.9, floor - h * 0.06, x - dir * len, floor + 2)
  ctx.closePath(); ctx.fill()
  // 先端の面: 進む側だけ、割れた石の明るい断面 (ぎざぎざの細い面) と、その裏の赤く灼けた面。「ここが先頭」を輪郭で読ませる。
  ctx.fillStyle = 'rgba(232,70,62,0.9)'
  ctx.beginPath()
  ctx.moveTo(footX - dir * 3, floor + 2)
  ctx.lineTo(tipX - dir * 5, tipY + h * 0.1)
  ctx.lineTo(tipX - dir * 12, tipY + h * 0.24)
  ctx.lineTo(footX - dir * 9, floor + 2)
  ctx.closePath(); ctx.fill()
  ctx.fillStyle = 'rgb(186,178,186)'
  ctx.beginPath()
  ctx.moveTo(footX, floor + 2)
  ctx.lineTo(footX + dir * 4.5, floor - h * 0.34)
  ctx.lineTo(footX + dir * 2.5, floor - h * 0.4)
  ctx.lineTo(footX + dir * 7.5, floor - h * 0.72)
  ctx.lineTo(footX + dir * 5.5, floor - h * 0.76)
  ctx.lineTo(tipX, tipY)
  ctx.lineTo(tipX - dir * 5, tipY + h * 0.1)
  ctx.lineTo(footX - dir * 3, floor + 2)
  ctx.closePath(); ctx.fill()
  // 割れ目 (暗い面) を波の上に 2 本入れて「一枚の三角」に見せない。
  ctx.fillStyle = 'rgba(24,20,28,0.85)'
  for (let i = 0; i < 2; i++) {
    const bx = x - dir * (20 + i * 22), w = 3.5 - i
    ctx.beginPath()
    ctx.moveTo(bx, floor + 2)
    ctx.lineTo(bx + dir * w, floor - h * (0.56 - i * 0.16))
    ctx.lineTo(bx + dir * w * 2.6, floor - h * (0.52 - i * 0.16))
    ctx.lineTo(bx + dir * w * 2.2, floor + 2)
    ctx.closePath(); ctx.fill()
  }
  // 跳ねる破片 (回る三角)。先端の近くほど大きく高い。
  for (let i = 0; i < 7; i++) {
    const k = hash01(seed + i)
    const px = x - dir * (k * len * 0.75 - 16), py = floor - h * (0.85 + hash01(seed + i * 3.7) * 1.2) * (1 - k * 0.35)
    const sz = 3 + (1 - k) * 5
    const rot = seed + i * 1.9 + traveled * 0.02 * (i % 2 ? 1 : -1)
    ctx.fillStyle = i % 3 === 0 ? 'rgba(196,188,192,0.95)' : 'rgba(132,124,130,0.92)'
    ctx.beginPath()
    for (let v = 0; v < 3; v++) {
      const a = rot + v * 2.094, rr = sz * (v === 0 ? 1 : 0.62)
      const vx = px + Math.cos(a) * rr, vy = py + Math.sin(a) * rr
      if (v === 0) ctx.moveTo(vx, vy); else ctx.lineTo(vx, vy)
    }
    ctx.closePath(); ctx.fill()
  }
}

/** 案b「呪いの滲み」: 床の下から赤黒い呪いが噴き出して走る。介錯の地割り (採用案a) と同じ語彙。 */
function drawWaveCurse(ctx: CanvasRenderingContext2D, x: number, floor: number, dir: number, seed: number, traveled: number): void {
  const len = Math.min(WAVE_TAIL, 44 + traveled * 0.45)
  ctx.globalCompositeOperation = 'source-over'
  // 外側 = 暗い赤黒 (面)。
  ctx.fillStyle = 'rgba(96,12,20,0.82)'
  crestPath(ctx, x, floor, dir, len, WAVE_H)
  ctx.fill()
  // 内側 = 一回り小さい明るい赤 (面)。
  ctx.fillStyle = 'rgba(176,24,34,0.85)'
  crestPath(ctx, x, floor, dir, len * 0.66, WAVE_H * 0.7)
  ctx.fill()
  // 芯の熱 (小面積・先端)。
  ctx.globalCompositeOperation = 'lighter'
  flatMote(ctx, x + dir * 3, floor - WAVE_H * 0.5, 20, 20, 'rgb(225,58,58)', 0.6)
  flatMote(ctx, x + dir * 3, floor - WAVE_H * 0.4, 9, 9, 'rgb(255,138,74)', 0.6)
  // 湧いて散る粒。
  for (let i = 0; i < 5; i++) {
    const h = hash01(seed + i * 2.3)
    glowMote(ctx, x - dir * len * h * 0.8, floor - WAVE_H * (0.7 + h * 1.2), 3 + h * 5, 'rgb(225,58,58)', 0.34 * (1 - h))
  }
}

/** 案c「魂の奔流」: 怨念と同じ紫の魂が床を走り、先端だけ赤く灼ける。王の技として色が揃う。 */
function drawWaveSoul(ctx: CanvasRenderingContext2D, x: number, floor: number, dir: number, seed: number, traveled: number): void {
  const len = Math.min(WAVE_TAIL, 44 + traveled * 0.5)
  ctx.globalCompositeOperation = 'source-over'
  ctx.fillStyle = 'rgba(58,26,92,0.75)'
  crestPath(ctx, x, floor, dir, len, WAVE_H)
  ctx.fill()
  ctx.fillStyle = 'rgba(141,85,187,0.85)'
  crestPath(ctx, x, floor, dir, len * 0.7, WAVE_H * 0.72)
  ctx.fill()
  // 芯 = 赤 (弾けない印)。紫の中の赤で「これは弾けない」を残す。
  ctx.globalCompositeOperation = 'lighter'
  flatMote(ctx, x + dir * 3, floor - WAVE_H * 0.45, 19, 18, 'rgb(216,50,58)', 0.7)
  flatMote(ctx, x + dir * 3, floor - WAVE_H * 0.36, 8, 8, 'rgb(255,220,206)', 0.6)
  // 立ち昇る魂の粒。
  for (let i = 0; i < 6; i++) {
    const h = hash01(seed + i * 5.1)
    glowMote(ctx, x - dir * len * h * 0.8, floor - WAVE_H * (0.8 + h * 1.4), 2.5 + h * 4, 'rgb(210,176,255)', 0.42 * (1 - h))
  }
}

// ===== ⑤ ピカン (跳び出しの予告) =====

/** 顔から一瞬走る光。progress 0→1 で立ち上がって消える。color: 赤=弾けない床技 / 白=弾ける締めの噛み。 */
export function drawRampageGlint(
  ctx: CanvasRenderingContext2D, x: number, y: number, progress: number, color: 'red' | 'white',
): void {
  const p = clamp01(progress)
  // 立ち上がりは鋭く、引きは長く。
  const grow = p < 0.3 ? p / 0.3 : 1 - (p - 0.3) / 0.7
  const core = color === 'red' ? 'rgb(255,74,58)' : 'rgb(255,255,255)'
  const halo = color === 'red' ? 'rgb(255,60,40)' : 'rgb(255,245,210)'
  ctx.save()
  ctx.globalCompositeOperation = 'lighter'
  if (glintLook === 'now') drawGlintNow(ctx, x, y, grow, core, color)
  else if (glintLook === 'a') drawGlintEye(ctx, x, y, grow, core, halo)
  else if (glintLook === 'b') drawGlintRing(ctx, x, y, p, grow, core, halo)
  else drawGlintBurst(ctx, x, y, p, core, halo)
  ctx.restore()
}

/** 現状 (2026-09-20 まで): 顔から十字の光条。線状の4本。 */
function drawGlintNow(ctx: CanvasRenderingContext2D, x: number, y: number, grow: number, core: string, color: 'red' | 'white'): void {
  const len = 30 + 90 * grow
  const halo = color === 'red' ? 'rgba(255,60,40,' : 'rgba(255,245,210,'
  const rg = ctx.createRadialGradient(x, y, 0, x, y, len * 0.55)
  rg.addColorStop(0, halo + (0.9 * grow).toFixed(3) + ')')
  rg.addColorStop(1, halo + '0)')
  ctx.fillStyle = rg
  ctx.beginPath(); ctx.arc(x, y, len * 0.55, 0, Math.PI * 2); ctx.fill()
  ctx.fillStyle = core
  ctx.globalAlpha = Math.max(0, grow)
  for (const [dx, dy, l, w] of [[1, 0, len, 5], [0, 1, len * 0.7, 5], [0.7, 0.7, len * 0.35, 3], [0.7, -0.7, len * 0.35, 3]] as const) {
    ctx.beginPath()
    ctx.moveTo(x - dx * l, y - dy * l)
    ctx.lineTo(x - dy * w, y + dx * w)
    ctx.lineTo(x + dx * l, y + dy * l)
    ctx.lineTo(x + dy * w, y - dx * w)
    ctx.closePath(); ctx.fill()
  }
}

/** 案a「目の閃光」: 光条を出さず、顔の虚空だけが一瞬強く灼ける。粒が数粒散る。いちばん控えめ。 */
function drawGlintEye(ctx: CanvasRenderingContext2D, x: number, y: number, grow: number, core: string, halo: string): void {
  const g = Math.max(0, grow)
  glowMote(ctx, x, y, 62 * (0.5 + 0.5 * g), halo, 0.5 * g)
  glowMote(ctx, x, y, 26 * (0.6 + 0.4 * g), halo, 0.7 * g)
  glowMote(ctx, x, y, 11 * (0.7 + 0.3 * g), core, 0.95 * g)
  for (let i = 0; i < 6; i++) {
    const a = i / 6 * Math.PI * 2 + 0.4
    const d = 18 + 34 * g
    glowMote(ctx, x + Math.cos(a) * d, y + Math.sin(a) * d * 0.7, 3 + 3 * g, core, 0.5 * g)
  }
}

/** 案b「輪の破裂」: 芯が灯り、太い光の輪が一度だけ外へ抜ける。予告として読みやすい。 */
function drawGlintRing(ctx: CanvasRenderingContext2D, x: number, y: number, p: number, grow: number, core: string, halo: string): void {
  const g = Math.max(0, grow)
  glowMote(ctx, x, y, 34 * (0.6 + 0.4 * g), halo, 0.55 * g)
  glowMote(ctx, x, y, 13 * (0.7 + 0.3 * g), core, 0.95 * g)
  // 輪 = 面で描く (外円を塗り、内円を destination-out で抜く方式は重ねると濁るので、粒を円周に並べる)。
  const ringR = 16 + 78 * p
  const ringA = 0.75 * (1 - p) * (1 - p)
  const n = 22
  for (let i = 0; i < n; i++) {
    const a = i / n * Math.PI * 2
    glowMote(ctx, x + Math.cos(a) * ringR, y + Math.sin(a) * ringR * 0.82, 7 + 5 * (1 - p), halo, ringA)
  }
}

/** 案c「溜めて弾ける」: 小さく灯る → 膨らんで弾け、粒が前方へ散る。合図が2段階で見える。 */
function drawGlintBurst(ctx: CanvasRenderingContext2D, x: number, y: number, p: number, core: string, halo: string): void {
  if (p < 0.45) {
    // 溜め: 小さな芯がじわりと明るくなる。
    const q = p / 0.45
    glowMote(ctx, x, y, 14 + 10 * q, halo, 0.3 + 0.35 * q)
    glowMote(ctx, x, y, 5 + 5 * q, core, 0.6 + 0.4 * q)
    return
  }
  // 破裂: 一気に膨らみ、粒が散って消える。
  const q = (p - 0.45) / 0.55
  const fade = (1 - q) * (1 - q)
  glowMote(ctx, x, y, 30 + 66 * q, halo, 0.6 * fade)
  glowMote(ctx, x, y, 12 + 16 * q, core, 0.9 * fade)
  for (let i = 0; i < 9; i++) {
    const a = hash01(i * 3.7) * Math.PI * 2
    const d = (20 + 90 * q) * (0.5 + hash01(i * 1.9) * 0.8)
    glowMote(ctx, x + Math.cos(a) * d, y + Math.sin(a) * d * 0.75, 3 + 5 * fade, core, 0.7 * fade)
  }
}

// ===== ⑥ 怨念 (王が壁を蹴るたびに残す飛び道具) =====
//   状態は4つ: 出たばかりで無害 (armed 前) / 弾ける紫 / 弾けない赤 / 弾き返した黄緑。
//   同時に6個前後が飛ぶので、1個が濃すぎると画面が潰れる。旧＝紫の玉＋進行方向の短い線。

type SpiritTone = { core: string; glow: string; dim: string }
const spiritTone = (red: boolean, reflected: boolean): SpiritTone => reflected
  ? { core: 'rgb(238,255,190)', glow: 'rgb(181,236,102)', dim: 'rgb(120,160,70)' }
  : red
    ? { core: 'rgb(255,208,196)', glow: 'rgb(216,50,58)', dim: 'rgb(106,30,36)' }
    // 弾ける紫: 紫の技を替える比較版 (DEV ?purpleSwap・src/purple_swap.ts) では 'parry' の色へ写す。赤・黄緑はそのまま。
    : { core: swc('parry', 'rgb(238,214,255)'), glow: swc('parry', 'rgb(141,85,187)'), dim: swc('parry', 'rgb(73,49,89)') }

export function drawRampageSpirit(ctx: CanvasRenderingContext2D, hit: ActiveHit, time: number): void {
  const s = hit.spirit!
  const r = (hit.widthPx ?? 26) / 2
  const flyingMs = hit.elapsedMs - (hit.launchDelayMs ?? 0)
  const armed = flyingMs >= 0
  const fade = hit.reflected ? 1 : Math.max(0.15, Math.min(1, (s.rangePx - s.traveledPx) / 40))
  const red = !hit.parryable && !hit.reflected
  const tone = spiritTone(red, !!hit.reflected)
  // 漂い = 見た目だけずらす (2026-09-20 ユーザー「見た目だけ」)。当たりは hit.x/y のまま。
  const drift = spiritDriftAt(time, hit.id, r, Math.atan2(hit.vy ?? 0, hit.vx ?? 1), flyingMs)
  const x = hit.x! + drift.dx, y = hit.y! + drift.dy
  const ang = Math.atan2(hit.vy ?? 0, hit.vx ?? 1) + drift.rot
  ctx.save()
  ctx.globalAlpha = fade
  const body = spiritBody(red, !!hit.reflected)
  if (spiritLook === 'art' && drawSpiritArt(ctx, x, y, r, time, hit.id, armed, ang, red, !!hit.reflected, tone, flyingMs)) {
    if (!hit.reflected) drawSpiritLaunchCue(ctx, x, y, r, flyingMs, tone)
    ctx.restore(); return
  }
  if (spiritLook === 'now') drawSpiritNow(ctx, x, y, r, time, hit.id, armed, ang, red, !!hit.reflected)
  else if (spiritLook === 'a') drawSpiritEmber(ctx, x, y, r, time, hit.id, armed, ang, tone, body)
  else if (spiritLook === 'b') drawSpiritShard(ctx, x, y, r, time, hit.id, armed, ang, tone, body)
  else drawSpiritVoid(ctx, x, y, r, time, hit.id, armed, ang, tone, body)
  ctx.restore()
}

/** 現状 (2026-09-20 まで): 紫の玉 + 進行方向の短い線。無害の間は点線。 */
function drawSpiritNow(ctx: CanvasRenderingContext2D, x: number, y: number, r: number, time: number, id: number,
  armed: boolean, ang: number, red: boolean, reflected: boolean): void {
  const pulse = 0.85 + 0.15 * Math.sin(time * 9 + id)
  ctx.setLineDash(armed ? [] : [3, 4])
  ctx.fillStyle = reflected ? '#b5ec66' : red ? (armed ? '#d8323a' : '#6a1e24') : armed ? swc('parry', '#8d55bb') : swc('parry', '#493159')
  ctx.strokeStyle = reflected ? '#eaffaf' : red ? '#ffb0a8' : swc('parry', '#d2b0ff')
  ctx.lineWidth = 2
  ctx.beginPath(); ctx.arc(x, y, r * pulse, 0, Math.PI * 2); ctx.fill(); ctx.stroke()
  ctx.setLineDash([])
  ctx.beginPath(); ctx.moveTo(x, y); ctx.lineTo(x + Math.cos(ang) * r * 1.8, y + Math.sin(ang) * r * 1.8); ctx.stroke()
}

/**
 * 採用の見た目 (2026-09-20): 床の帯と同じ画風で描き起こした塊を、飛ぶ向きへ回して貼る。
 *   原画は頭が左・尾が右。絵が未読み込みなら false を返して従来の描画へ落ちる。
 *   出たばかり (armed 前) は薄く大きく = まだ固まっていない。
 */
function drawSpiritArt(ctx: CanvasRenderingContext2D, x: number, y: number, r: number, time: number, id: number,
  armed: boolean, ang: number, red: boolean, reflected: boolean, tone: SpiritTone, flyingMs: number): boolean {
  const img = getGrudgeOrb(reflected ? 'lime' : red ? 'red' : 'violet')
  if (!img || !img.complete || img.naturalWidth <= 0) return false
  const sheet = spiritArtSheet && spiritAnim !== 'now' ? getGrudgeSheet(reflected ? 'lime' : red ? 'red' : 'violet') : null
  if (sheet && sheet.complete && sheet.naturalWidth > 0) {
    drawSpiritAnimated(ctx, sheet, sheet.naturalWidth / GRUDGE_SHEET_FRAMES, sheet.naturalHeight,
      x, y, r, time, id, armed, ang, tone, flyingMs, sheetFrameAt(time, id), SHEET_ANCHOR)
    return true
  }
  if (spiritAnim !== 'now') {
    drawSpiritAnimated(ctx, !red && !reflected ? swapImage('parry', img) : img, img.naturalWidth, img.naturalHeight,
      x, y, r, time, id, armed, ang, tone, flyingMs)
    return true
  }
  // 当たりの帯 (幅 2r) に対して塊の「頭」がだいたい重なる寸法。絵は頭が左寄りなので少し大きめに貼る。
  //   09-20 ユーザー「もう少し大きくないと見えにくい」→ 4.2 倍から 5.6 倍へ (`?spiritSize=s|m|l`)。
  const w = r * (SPIRIT_SIZE_MUL[spiritSizeKey] ?? 5.6) * (armed ? 1 : 1.15)
  const h = w * (img.naturalHeight / img.naturalWidth)
  const breathe = 1 + 0.04 * Math.sin(time * 6 + id)
  ctx.save()
  ctx.translate(x, y)
  ctx.rotate(ang)
  // 原画は頭が左なので左右を返し、頭 (光る芯) を進行方向 = +x へ向ける (2026-09-21 ユーザー「弾が前で尻尾が後ろ」)。
  ctx.scale(-breathe, breathe)
  ctx.globalAlpha *= armed ? 1 : 0.5
  // 芯 (原画の左から約34%) を当たりの中心へ重ね、尾は後ろへ流す。
  // 弾ける紫の玉だけ、比較版 (DEV ?purpleSwap) では 'parry' の色へ写した絵 (元の grudge_orb_violet.png は触らない)。
  ctx.drawImage(!red && !reflected ? swapImage('parry', img) : img, -w * 0.34, -h / 2, w, h)
  ctx.restore()
  if (!armed) {
    // 固まる前は輪郭をぼかす代わりに、同じ色の光を薄く重ねる。
    ctx.globalCompositeOperation = 'lighter'
    glowMote(ctx, x, y, r * 2.4, tone.glow, 0.22)
  }
  return true
}

/** 怨念を「絵を動かしている」から「生きて揺らぐ塊」へ (11-01 作者「怨念のアニメーションは今ただ画像を動かしている感じがあるからもっとリッチにできるはず」)。
 *  配布版の既定 = b (11-01 作者の答え spirit=b)。前の版は開発版 `?spiritAnim=now` (絵1枚を回して 4% 息をするだけ)。判定・秒数は変えない (描くだけ)。
 *    a = 絵を縦に細く切り、尾ほど大きくうねらせる (波は頭から尾へ流れる) ＋ 芯の明滅 (不規則に強まる光)。
 *    b = a ＋ 飛ぶ間の残像3枚 ＋ 尾から剥がれて後ろへ流れる雫。 */
export type RampageSpiritAnim = 'now' | 'a' | 'b'
const SPIRIT_ANIMS: readonly string[] = ['now', 'a', 'b']
let spiritAnim: RampageSpiritAnim = (() => {
  if (!import.meta.env.DEV || typeof window === 'undefined') return 'b'
  const v = new URLSearchParams(window.location.search).get('spiritAnim')
  return SPIRIT_ANIMS.includes(v ?? '') ? v as RampageSpiritAnim : 'b'
})()
export function setRampageSpiritAnim(a: RampageSpiritAnim): void { spiritAnim = a }
// 怨念の6コマの絵 (11-01 作者 art=sample → 型の答え ok で配布版)。紫・赤・黄緑ともコマ送りの絵にする。
// 開発版 ?spiritArt=orb で前の1枚の絵 (?spiritAnim=now の時も前の版のまま1枚)。
let spiritArtSheet = !(import.meta.env.DEV && typeof window !== 'undefined' && new URLSearchParams(window.location.search).get('spiritArt') === 'orb')
if (spiritArtSheet) preloadGrudgeSheet()
export function setRampageSpiritArtSheet(on: boolean): void { spiritArtSheet = on; if (on) preloadGrudgeSheet() }
export function getRampageSpiritArtSheet(): boolean { return spiritArtSheet }
export function getRampageSpiritAnim(): RampageSpiritAnim { return spiritAnim }

const ANIM_SLICES = 22
// 絵の中の位置 (幅・高さに対する割合)。芯 = 光る点、尾 = 右端の流れ。貼る基準 (当たりの中心) は左から 34%。
const ART_ANCHOR_U = 0.34, ART_CORE_U = 0.18, ART_CORE_V = 0.53
// 6コマの絵 (grudge_sheet_violet_1101) は1コマの幅の 76% に体が収まる (今の絵は幅いっぱい)。同じ大きさに見えるよう 1.32 倍で貼り、
// 当たりの中心は芯から今の絵と同じ距離だけ右 (今の絵の幅の 0.16 = 型の幅の 0.121)。
type ArtAnchor = { anchor: number; core: number; scale: number }
const ORB_ANCHOR: ArtAnchor = { anchor: ART_ANCHOR_U, core: ART_CORE_U, scale: 1 }
const SHEET_ANCHOR: ArtAnchor = { anchor: 0.346, core: 0.225, scale: 1.32 }

/** 絵を縦の帯に切って、尾ほど上下へうねらせて貼る。原点 = 当たりの中心、+x = 絵の右 (尾) 側 (呼ぶ側で左右を返している)。 */
function drawWarpedArt(ctx: CanvasRenderingContext2D, img: CanvasImageSource, nw: number, nh: number,
  w: number, h: number, time: number, id: number, amp: number, sx0 = 0, anchorU = ART_ANCHOR_U): void {
  const sw = nw / ANIM_SLICES, dw = w / ANIM_SLICES
  for (let i = 0; i < ANIM_SLICES; i++) {
    const u = i / ANIM_SLICES
    const tail = clamp01((u - 0.22) / 0.78)
    const k = tail * Math.sqrt(tail)
    // 波は頭から尾へ流れる。尾の方は細かい揺れも重ねる (布でなく煙・液の動き)。
    const off = h * amp * k * (Math.sin(time * 7.2 - u * 8.5 + id * 1.3) + 0.35 * Math.sin(time * 13.1 - u * 17 + id))
    const stretch = 1 + amp * 0.9 * k * Math.sin(time * 5.3 - u * 6 + id * 0.7)
    const dh = h * stretch
    ctx.drawImage(img, sx0 + i * sw, 0, i === ANIM_SLICES - 1 ? sw : sw + 1, nh, -w * anchorU + i * dw, -dh / 2 + off, dw + 0.8, dh)
  }
}

/** 芯の明滅。正弦の重ね＋時々の強い瞬き (1秒に数回・個体ごとにずれる)。 */
function coreFlicker(time: number, id: number): number {
  const base = 0.55 + 0.22 * Math.sin(time * 23 + id) + 0.15 * Math.sin(time * 37.7 + id * 2.1)
  const spike = hash01(Math.floor(time * 11) * 7.13 + id * 3.1) > 0.82 ? 0.55 : 0
  return base + spike
}

/** 型のコマ (1秒に SHEET_FPS コマ・個体ごとにずらす)。 */
const SHEET_FPS = 10
function sheetFrameAt(time: number, id: number): number {
  return (Math.floor(time * SHEET_FPS) + id * 2) % GRUDGE_SHEET_FRAMES
}

function drawSpiritAnimated(ctx: CanvasRenderingContext2D, img: CanvasImageSource, nw: number, nh: number, x: number, y: number, r: number, time: number, id: number,
  armed: boolean, ang: number, tone: SpiritTone, flyingMs: number, frame = 0, at: ArtAnchor = ORB_ANCHOR): void {
  const sx0 = frame * nw
  const w = r * (SPIRIT_SIZE_MUL[spiritSizeKey] ?? 5.6) * (armed ? 1 : 1.15) * at.scale
  const h = w * (nh / nw)
  const rich = spiritAnim === 'b'
  const amp = rich ? 0.085 : 0.06
  const alpha = ctx.globalAlpha * (armed ? 1 : 0.5)
  const breathe = 1 + 0.04 * Math.sin(time * 6 + id)
  // 残像 (b・飛んでいる間だけ)。少し前の時刻の揺れで、進む向きの後ろへ並べる。
  if (rich && flyingMs > 0) {
    const grow = clamp01(flyingMs / 120)
    for (let k = 3; k >= 1; k--) {
      ctx.save()
      ctx.translate(x - Math.cos(ang) * r * 1.25 * k, y - Math.sin(ang) * r * 1.25 * k)
      ctx.rotate(ang)
      ctx.scale(-breathe * (1 + 0.05 * k), breathe * (1 + 0.05 * k))
      ctx.globalAlpha = alpha * grow * [0, 0.26, 0.15, 0.08][k]
      drawWarpedArt(ctx, img, nw, nh, w, h, time - k * 0.05, id, amp, sx0, at.anchor)
      ctx.restore()
    }
  }
  ctx.save()
  ctx.translate(x, y)
  ctx.rotate(ang)
  ctx.scale(-breathe, breathe)
  ctx.globalAlpha = alpha
  drawWarpedArt(ctx, img, nw, nh, w, h, time, id, amp, sx0, at.anchor)
  // 芯の明滅 (絵の光る点の上に、同じ色の光を足す)。
  const f = coreFlicker(time, id)
  const cx = -w * (at.anchor - at.core), cy = h * (ART_CORE_V - 0.5)
  glowMote(ctx, cx, cy, r * (1.6 + 0.5 * f), tone.glow, 0.32 * f)
  glowMote(ctx, cx, cy, r * (0.55 + 0.25 * f), tone.core, 0.55 * f)
  // 尾から剥がれる雫 (b)。尾の先から生まれ、後ろへ流れながら縮んで消える。状態を持たず時刻から決める。
  if (rich) {
    for (let i = 0; i < 7; i++) {
      const period = 0.55 + hash01(i * 5.3 + id) * 0.35
      const t = ((time + hash01(i * 2.9 + id * 1.7) * period) / period) % 1
      const born = Math.floor((time + hash01(i * 2.9 + id * 1.7) * period) / period)
      const lane = (hash01(born * 3.3 + i + id) - 0.5) * h / at.scale * 0.55
      const px = w / at.scale * (0.32 + 0.1 * hash01(born + i * 1.1)) + t * r * (armed ? 3.4 : 1.6)
      const py = lane + Math.sin(time * 6 + i) * r * 0.25 * t
      // 平たい丸に見えないよう、流れる向きへ細長く・薄く。芯だけ少し明るい。
      const s = r * (0.2 - 0.13 * t)
      const a = alpha * (1 - t) * (t < 0.12 ? t / 0.12 : 1)
      ctx.globalAlpha = a * 0.55
      ctx.fillStyle = tone.dim
      ctx.beginPath(); ctx.ellipse(px, py, s * 2.4, s * 0.6, 0, 0, Math.PI * 2); ctx.fill()
      glowMote(ctx, px, py, s * 2.6, tone.glow, 0.3 * a)
      glowMote(ctx, px - s * 0.8, py, s * 0.7, tone.core, 0.5 * a)
    }
  }
  ctx.restore()
  if (!armed) {
    ctx.save()
    ctx.globalCompositeOperation = 'lighter'
    glowMote(ctx, x, y, r * 2.4, tone.glow, 0.22)
    ctx.restore()
  }
}

/** 放たれる瞬間の合図 (09-21 総評 A-2「発射の瞬間に合図がない」)。
 *  放つ 0.3 秒前から芯が脈打って明るくなり、放った瞬間に白い芯が一閃して 0.18 秒で消える。面と光だけ。 */
function drawSpiritLaunchCue(ctx: CanvasRenderingContext2D, x: number, y: number, r: number, flyingMs: number, tone: SpiritTone): void {
  if (flyingMs < -300 || flyingMs > 180) return
  ctx.save()
  ctx.globalCompositeOperation = 'lighter'
  if (flyingMs < 0) {
    const k = 1 + flyingMs / 300                       // 0→1 で放つ瞬間へ
    const pulse = 0.55 + 0.45 * Math.sin(k * k * 22)
    glowMote(ctx, x, y, r * (1.2 + k * 1.4), tone.glow, 0.3 * k * pulse)
    glowMote(ctx, x, y, r * (0.5 + k * 0.5), 'rgb(255,250,245)', 0.5 * k * pulse)
  } else {
    const k = flyingMs / 180                           // 0→1 で消える
    glowMote(ctx, x, y, r * (2.2 + k * 3.2), tone.glow, 0.55 * (1 - k))
    glowMote(ctx, x, y, r * (1.5 - k * 0.7), 'rgb(255,250,245)', 0.95 * (1 - k) * (1 - k))
  }
  ctx.restore()
}

// 3案に共通の語彙 = 覚醒王の既存「大暴れ」の床の帯 (`rampage_grudge_b.png`) と同じ作り。
//   ＝暗く粘る塊 / その内側に光る筋 / 弾ける火花。色だけ役割で変える (紫=弾ける・赤=弾けない・黄緑=返弾)。

/** 内側の光る筋 (面を細長く潰して描く)。角度は塊の流れに沿わせる。 */
function vein(ctx: CanvasRenderingContext2D, x: number, y: number, len: number, thick: number,
  angle: number, color: string, a: number): void {
  ctx.save()
  ctx.translate(x, y)
  ctx.rotate(angle)
  ctx.scale(1, thick / len)
  glowMote(ctx, 0, 0, len, color, a)
  ctx.restore()
}

/** 暗く粘る塊の本体 (床の帯と同じ「黒みの強い赤紫」)。 */
const spiritBody = (red: boolean, reflected: boolean): string => reflected
  ? 'rgba(26,34,12,0.88)' : red ? 'rgba(34,8,12,0.88)' : swc('parry', 'rgba(24,12,34,0.88)')

/** 案a「渦」: 床の帯の巻き上がりをそのまま小さくした形。暗い塊が巻き、内側に筋が1本光る。 */
function drawSpiritEmber(ctx: CanvasRenderingContext2D, x: number, y: number, r: number, time: number, id: number,
  armed: boolean, ang: number, tone: SpiritTone, body: string): void {
  const flick = 0.9 + 0.1 * Math.sin(time * 11 + id)
  ctx.globalCompositeOperation = 'lighter'
  glowMote(ctx, x, y, r * (armed ? 2.1 : 2.6), tone.glow, armed ? 0.34 : 0.2)
  ctx.globalCompositeOperation = 'source-over'
  ctx.globalAlpha *= armed ? 1 : 0.5
  // 巻いた塊 (面)。頭が太く、尾が細く後ろへ流れる。
  ctx.fillStyle = body
  ctx.save()
  ctx.translate(x, y); ctx.rotate(ang)
  ctx.beginPath()
  ctx.moveTo(r * 0.95, 0)
  ctx.quadraticCurveTo(r * 0.3, -r * 1.15, -r * 0.8, -r * 0.55)
  ctx.quadraticCurveTo(-r * 1.9, -r * 0.05, -r * 0.6, r * 0.45)
  ctx.quadraticCurveTo(r * 0.1, r * 0.95, r * 0.95, 0)
  ctx.closePath(); ctx.fill()
  ctx.restore()
  ctx.globalCompositeOperation = 'lighter'
  // 内側の筋と火花。
  vein(ctx, x, y, r * 0.95, r * 0.26, ang - 0.5, tone.core, 0.85 * flick)
  vein(ctx, x - Math.cos(ang) * r * 0.6, y - Math.sin(ang) * r * 0.6, r * 0.5, r * 0.16, ang + 0.4, tone.glow, 0.6)
  if (!armed) return
  for (let i = 1; i <= 2; i++) {
    const d = r * (1.5 + i * 0.7), wob = Math.sin(time * 8 + id + i) * r * 0.4
    glowMote(ctx, x - Math.cos(ang) * d - Math.sin(ang) * wob, y - Math.sin(ang) * d + Math.cos(ang) * wob,
      r * 0.26, tone.core, 0.4 / i)
  }
}

/** 案b「滴」: 塊を丸く濃く固めた版。芯が強く光るので、数が出ても1個ずつ位置が読める。 */
function drawSpiritShard(ctx: CanvasRenderingContext2D, x: number, y: number, r: number, time: number, id: number,
  armed: boolean, ang: number, tone: SpiritTone, body: string): void {
  const flick = 0.88 + 0.12 * Math.sin(time * 9 + id)
  ctx.globalCompositeOperation = 'lighter'
  glowMote(ctx, x, y, r * (armed ? 1.9 : 2.5), tone.glow, armed ? 0.38 : 0.2)
  ctx.globalCompositeOperation = 'source-over'
  ctx.globalAlpha *= armed ? 1 : 0.5
  ctx.fillStyle = body
  ctx.save()
  ctx.translate(x, y); ctx.rotate(ang)
  // 進む側が丸く、後ろへ尾を引く滴。
  ctx.beginPath()
  ctx.moveTo(r * 1.0, 0)
  ctx.quadraticCurveTo(r * 0.7, -r * 0.95, -r * 0.35, -r * 0.7)
  ctx.quadraticCurveTo(-r * 1.6, -r * 0.3, -r * 1.7, 0)
  ctx.quadraticCurveTo(-r * 1.6, r * 0.3, -r * 0.35, r * 0.7)
  ctx.quadraticCurveTo(r * 0.7, r * 0.95, r * 1.0, 0)
  ctx.closePath(); ctx.fill()
  ctx.restore()
  ctx.globalCompositeOperation = 'lighter'
  vein(ctx, x + Math.cos(ang) * r * 0.1, y + Math.sin(ang) * r * 0.1, r * 0.8, r * 0.34, ang, tone.core, 0.9 * flick)
  glowMote(ctx, x + Math.cos(ang) * r * 0.45, y + Math.sin(ang) * r * 0.45, r * 0.3, tone.core, 0.8)
  if (!armed) return
  glowMote(ctx, x - Math.cos(ang) * r * 2.0, y - Math.sin(ang) * r * 2.0, r * 0.28, tone.glow, 0.34)
}

/** 案c「群れ」: 小さな塊が2つ重なって脈打つ。床の帯の「ぼこぼこ沸く」感じが一番近い。 */
function drawSpiritVoid(ctx: CanvasRenderingContext2D, x: number, y: number, r: number, time: number, id: number,
  armed: boolean, ang: number, tone: SpiritTone, body: string): void {
  const beat = Math.sin(time * 7 + id)
  ctx.globalCompositeOperation = 'lighter'
  glowMote(ctx, x, y, r * (armed ? 2.2 : 2.7), tone.glow, armed ? 0.32 : 0.18)
  ctx.globalCompositeOperation = 'source-over'
  ctx.globalAlpha *= armed ? 1 : 0.5
  ctx.fillStyle = body
  const blobs: [number, number, number][] = [
    [r * 0.35, -r * 0.15, r * 0.86 + beat * r * 0.06],
    [-r * 0.5, r * 0.25, r * 0.62 - beat * r * 0.06],
    [-r * 0.15, -r * 0.5, r * 0.45 + beat * r * 0.04],
  ]
  for (const [dx, dy, rad] of blobs) {
    const px = x + Math.cos(ang) * dx - Math.sin(ang) * dy
    const py = y + Math.sin(ang) * dx + Math.cos(ang) * dy
    ctx.beginPath(); ctx.arc(px, py, rad, 0, Math.PI * 2); ctx.fill()
  }
  ctx.globalCompositeOperation = 'lighter'
  vein(ctx, x + Math.cos(ang) * r * 0.3, y + Math.sin(ang) * r * 0.3, r * 0.6, r * 0.24, ang - 0.7, tone.core, 0.85)
  vein(ctx, x - Math.cos(ang) * r * 0.45, y - Math.sin(ang) * r * 0.45, r * 0.4, r * 0.18, ang + 0.6, tone.glow, 0.6)
  if (!armed) return
  glowMote(ctx, x - Math.cos(ang) * r * 1.9, y - Math.sin(ang) * r * 1.9, r * 0.24, tone.core, 0.34)
}

// ===== 体が壁・床に触れた瞬間の粒 (09-21 総評 B-1〜B-4) =====
//   FxEvent `rampage_contact` を描く。寿命は FX 共通の 0.5 秒。線は使わず、面 (土煙) と粒 (石の破片) だけ。
export type RampageContactVariant =
  | 'wall_kick' | 'wall_land' | 'ground_kick' | 'ground_land' | 'slam' | 'roar' | 'collapse' | 'orb_impact'
type ContactLook = { shards: number; speed: number; dust: number; dustR: number; spreadRad: number }
const CONTACT_LOOKS: Readonly<Record<Exclude<RampageContactVariant, 'orb_impact'>, ContactLook>> = {
  wall_kick:   { shards: 9,  speed: 300, dust: 3, dustR: 18, spreadRad: 1.0 },
  wall_land:   { shards: 7,  speed: 220, dust: 3, dustR: 20, spreadRad: 1.2 },
  ground_kick: { shards: 4,  speed: 170, dust: 2, dustR: 24, spreadRad: 0.7 },
  ground_land: { shards: 6,  speed: 210, dust: 4, dustR: 34, spreadRad: 0.9 },
  slam:        { shards: 16, speed: 420, dust: 7, dustR: 58, spreadRad: 1.2 },
  roar:        { shards: 10, speed: 230, dust: 8, dustR: 54, spreadRad: 1.3 },
  collapse:    { shards: 3,  speed: 120, dust: 5, dustR: 44, spreadRad: 0.8 },
}

/** x,y = 触れた点、dir = 壁なら部屋側 (±1)・床なら体の向き。age は秒。 */
export function drawRampageContact(ctx: CanvasRenderingContext2D, variant: RampageContactVariant,
  x: number, y: number, dir: number, age: number, seed: number): void {
  if (variant === 'orb_impact') { drawOrbImpact(ctx, x, y, age, seed); return }
  const look = CONTACT_LOOKS[variant]
  const onWall = variant === 'wall_kick' || variant === 'wall_land'
  const k = clamp01(age / 0.5), out = 1 - (1 - k) * (1 - k)
  ctx.save()
  // 土煙 (面)。壁は部屋側へ吹き出し、床は左右へ低く広がる。
  for (let i = 0; i < look.dust; i++) {
    const h = hash01(seed + i * 3.3), side = onWall ? 1 : (i % 2 ? 1 : -1)
    const reach = look.dustR * (0.5 + h) * (0.4 + out * 1.5)
    const dx = onWall ? dir * reach : side * reach * 1.3
    const dy = onWall ? (h - 0.5) * 110 - out * 14 : -look.dustR * (0.18 + h * 0.3) * (0.5 + out)
    const rr = look.dustR * (0.45 + h * 0.5) * (0.6 + out * 0.9)
    ctx.fillStyle = `rgba(126,116,120,${(0.34 * (1 - k) * (1 - k)).toFixed(3)})`
    ctx.beginPath()
    ctx.ellipse(x + dx, y + dy, rr * (onWall ? 0.8 : 1.35), rr * (onWall ? 1.1 : 0.6), 0, 0, Math.PI * 2)
    ctx.fill()
  }
  // 石の破片 (回る三角)。重力で落ちる。
  const base = onWall ? (dir > 0 ? 0 : Math.PI) : -Math.PI / 2
  for (let i = 0; i < look.shards; i++) {
    const h = hash01(seed + i * 7.7), h2 = hash01(seed + i * 1.3 + 5)
    const a = base + (h - 0.5) * 2 * look.spreadRad
    const v = look.speed * (0.45 + h2 * 0.75)
    const px = x + Math.cos(a) * v * age + (onWall ? 0 : (h2 - 0.5) * 60)
    const py = y + (onWall ? (h2 - 0.5) * 100 : 0) + Math.sin(a) * v * age + 520 * age * age
    const sz = (2.5 + h2 * 4.5) * (variant === 'slam' ? 1.35 : 1)
    const rot = seed + i + age * (8 + h * 10) * (i % 2 ? 1 : -1)
    ctx.fillStyle = `rgba(${i % 3 === 0 ? '204,196,200' : '140,132,140'},${(0.95 * (1 - k)).toFixed(3)})`
    ctx.beginPath()
    for (let c = 0; c < 3; c++) {
      const ca = rot + c * 2.094, rr = sz * (c === 0 ? 1 : 0.6)
      if (c === 0) ctx.moveTo(px + Math.cos(ca) * rr, py + Math.sin(ca) * rr)
      else ctx.lineTo(px + Math.cos(ca) * rr, py + Math.sin(ca) * rr)
    }
    ctx.closePath(); ctx.fill()
  }
  // 叩きつけだけ: 着地点の床に赤い熱が一瞬広がる (弾けない技の赤)。
  if (variant === 'slam') {
    ctx.globalCompositeOperation = 'lighter'
    flatMote(ctx, x, y - 4, 70 + out * 120, 12 + out * 8, 'rgb(232,70,62)', 0.6 * (1 - k))
  }
  ctx.restore()
}

/** 弾き返した黄緑が王に当たった瞬間: 黄緑の芯が弾け、粒が散る (Codex 優先4「王への着弾を一段だけ明確に」)。 */
function drawOrbImpact(ctx: CanvasRenderingContext2D, x: number, y: number, age: number, seed: number): void {
  const k = clamp01(age / 0.4)
  ctx.save()
  ctx.globalCompositeOperation = 'lighter'
  glowMote(ctx, x, y, 22 + k * 58, 'rgb(181,236,102)', 0.7 * (1 - k))
  glowMote(ctx, x, y, 12 + k * 14, 'rgb(244,255,214)', 0.95 * (1 - k) * (1 - k))
  for (let i = 0; i < 9; i++) {
    const a = hash01(seed + i) * Math.PI * 2, v = 120 + hash01(seed + i * 2.7) * 200
    glowMote(ctx, x + Math.cos(a) * v * age, y + Math.sin(a) * v * age + 200 * age * age, 3 + hash01(seed + i * 4.1) * 3,
      'rgb(214,255,150)', 0.85 * (1 - k))
  }
  ctx.restore()
}
